import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { DocumentType } from "domain/entities/document-type";
import { DocumentTypeInputFilter } from "domain/models/document-type-input-filter";
import { Logger } from "infra/logger/logger";
import documentTypeRepository from "infra/repositories/document-type-repository";

export const useDocumentTypeByFilter = (filter: DocumentTypeInputFilter, enabled: boolean = true) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["documentTypes", filter],
    queryFn: async () => {
      try {
        if (!filter.name && !filter.justCPStypes) return;
        const result = await documentTypeRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: 0,
    enabled: enabled
  });
};

export const useDocumentTypeByFilterAll = (filter: DocumentTypeInputFilter) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["documentTypes", filter],
    queryFn: async () => {
      try {
        const result = await documentTypeRepository.getAll(filter);
        return result;
      } catch (error: any) {
        if (error instanceof AxiosError) {
          logger.error(
            error.response?.data.metadata.errors[0],
            error.response?.data.metadata
          );
          throw new Error(error.response?.data.metadata.errors);
        }
        throw error;
      }
    },
    retry: Number(process.env.REACT_APP_QUERY_RETRY) || 5,
    staleTime: 0,
  });
};

export const useDeleteDocumentTypeMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (documentTypeId: string) => {
      if (!documentTypeId) throw new Error("ID inválido para exclusão");
      const result = await documentTypeRepository.delete(documentTypeId);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documentTypes"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata?.errors?.[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata?.errors);
      }
      throw error;
    },
  });
};

export const useCreateDocumentTypeMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: DocumentType) => {
      const result = await documentTypeRepository.create(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documentTypes"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        const errors = error.response?.data?.metadata?.errors;
        logger.error(errors?.[0], error.response?.data.metadata);
        const customError = new Error("Erro na criação do tipo de documento");
        (customError as any).errors = errors;
        throw customError;
      }
      throw error;
    },
  });
};

export const useUpdateDocumentTypeMutation = () => {
  const logger = Logger.getInstance();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: DocumentType) => {
      const result = await documentTypeRepository.update(data);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["documentTypes"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata?.errors?.[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata?.errors);
      }
      throw error;
    },
  });
};
